import React, { useEffect, useRef } from "react";
import clsx from "classnames";
import { useChannelStateContext } from "stream-chat-react";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { BOOKS_CHANNEL_CUSTOM_TYPE, TASK_CHANNEL } from "constants/channelType";
import { ROLE_TAG_TYPE } from "components/ChannelinfoSlider/ChannelInfo/TeamMembers/TeamMembers";
import { BOOKKEEPER, PREPARER, REVIEWER } from "constants/chatMemberType";

const shouldShowRoleTag = (roleName?: string, channelCustomType?: string) => {
  if (!roleName) return false;

  if (roleName === BOOKKEEPER) {
    return channelCustomType === BOOKS_CHANNEL_CUSTOM_TYPE;
  }

  if (roleName === PREPARER || roleName === REVIEWER) {
    return channelCustomType === TASK_CHANNEL;
  }

  return true;
};

type MentionUserItemProps = {
  entity: {
    itemNameParts: {
      match: string;
      parts: string[];
    };
    id?: string;
    image?: string;
    name?: string;
    role_name?: string;
    assigned_roles?: string[];
  };
  selected?: boolean;
};

export const MentionUserItem = React.memo(
  ({ entity, selected }: MentionUserItemProps) => {
    const { channel } = useChannelStateContext("MentionUserItem");
    const channelCustomType = channel?.data?.custom_type as string | undefined;
    const { isAdmin } = useRoleBasedView();
    const itemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (selected && itemRef.current) {
        itemRef.current.scrollIntoView({ block: "nearest" });
      }
    }, [selected]);

    const hasEntity = !!Object.keys(entity).length;
    const itemParts = entity?.itemNameParts;
    const roles = (entity?.assigned_roles || []).filter((role) => {
      if (!isAdmin && role === "Reviewer") return false;
      return shouldShowRoleTag(role, channelCustomType);
    });

    const renderName = () => {
      if (!hasEntity) return null;

      return itemParts.parts.map((part, i) => {
        const matches = part.toLowerCase() === itemParts.match.toLowerCase();
        return (
          <span
            className={clsx({
              "str-chat__emoji-item--highlight": matches,
              "str-chat__emoji-item--part": !matches,
            })}
            key={`part-${i}`}
          >
            {part}
          </span>
        );
      });
    };

    return (
      <div ref={itemRef} className="str-chat__user-item">
        <Avatar src={entity.image} alt={entity.name || ""} size="extra-small" />
        <span
          className="str-chat__user-item--name"
          data-testid="user-item-name"
        >
          {renderName()}
        </span>
        {roles.length > 0 && (
          <span className="t-shrink-0 t-flex t-gap-1">
            {roles.map((role) => (
              <Tag
                key={role}
                tagType={ROLE_TAG_TYPE[role] || "gray"}
                size="small"
                icon={false}
              >
                {role}
              </Tag>
            ))}
          </span>
        )}
      </div>
    );
  }
);
